import { useState } from "react";

import { supabase } from "../lib/supabase.ts";
import { PATHS } from "../routes/routes.constants.ts";
import { FORGOT_COPY } from "./passwordReset.constants.ts";
import { isThrottled, type ForgotView } from "./passwordReset.model.ts";

/**
 * Asking for a reset link.
 *
 * ## THE REDIRECT IS BUILT HERE, AND THE SCREEN IT NAMES MUST BE ON THE ALLOWLIST
 *
 * `resetPasswordForEmail` takes the address the emailed link should land on, and GoTrue
 * **silently falls back to `site_url` for anything not in `additional_redirect_urls`**.
 * The failure is not an error: the mail sends, the link works, and it lands on the
 * website's front door — where the browser client consumes the recovery fragment and
 * signs somebody in without ever showing them the form they asked for. Both local
 * origins are listed in `supabase/config.toml` with that paragraph beside them; a
 * deployed origin is added on the hosted project.
 *
 * `window.location.origin` rather than a configured value, so the link comes back to
 * the deployment it was asked from. A preview build emailing a link to production would
 * otherwise be a reset somebody could not finish.
 *
 * ## ONE OUTCOME, AND NOTHING FOR THE SCREEN TO BRANCH ON
 *
 * Supabase answers identically whether or not the address has an account, which is the
 * property the closed register depends on. The `sent` member of `ForgotView` therefore
 * carries no data at all — the screen has nowhere to put a verdict even by accident.
 *
 * The only failure that reaches the catch is about the REQUEST rather than the account,
 * realistically the per-hour sending budget, and it is shown as a wait.
 */
export function useForgotPasswordController() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const view: ForgotView = sent
    ? { kind: "sent" }
    : sending
      ? { kind: "sending", email }
      : { kind: "asking", email, canSubmit: email.trim() !== "", error };

  function changeEmail(next: string): void {
    setEmail(next);
    // Clearing on edit rather than on submit: an error about the previous attempt
    // sitting above a field somebody is retyping reads as being about what they are
    // typing now.
    setError(null);
  }

  async function submitRequest(): Promise<void> {
    if (sending || email.trim() === "") return;

    setSending(true);
    setError(null);

    const { error: failure } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}${PATHS.resetPassword}`,
    });

    if (failure) {
      setError(isThrottled(failure) ? FORGOT_COPY.throttled : FORGOT_COPY.failure);
      setSending(false);
      return;
    }

    setSending(false);
    setSent(true);
  }

  return { view, changeEmail, submitRequest };
}
